import { RouteComponentProps } from "@reach/router";
import {
  FormEvent,
  FunctionComponent,
  useContext,
  useEffect,
  useState
} from "react";
import { MapAcquirerUpdate, MapAcquirers } from "../utils/helpers/types";
import Table, { Column } from "../components/table/Table";
import styles from "./self-service.module.scss";
import ContextMenu, {
  ContextOption
} from "../components/context-menu/ContextMenu";
import { BooleanFilter } from "../utils/helpers/types-helpers";
import { initialPaginationData } from "../utils/constants";
import LayoutContext from "../context/layout-context";
import {
  getAcquirer,
  mapAcquirerToRID,
  updateMapAcquirer
} from "../utils/helpers/data/map-acquirer";
import Modal, { ModalProps } from "../components/modal/Modal";
import { resources } from "../utils/resources";
import Input from "../components/input/Input";
import style from "./history.module.scss";
import Button from "../components/button/Button";

const MapAcquirer: FunctionComponent<RouteComponentProps> = () => {
  let timerRef: NodeJS.Timeout;
  const { notify } = useContext(LayoutContext);
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [mapAcquirerOnEdit, setMapAcquirerOnEdit] =
    useState<MapAcquirers | null>(null);
  const [pagination, setPagination] = useState({ ...initialPaginationData });
  const [loading, setLoading] = useState(false);

  const [reset, setReset] = useState(false);

  const [rid, setRid] = useState<string>("");
  const [acquirerCode, setAcquirerCode] = useState<string>("");
  const [acquirerName, setAcquirerName] = useState<string>("");

  const showResetFilter = rid || acquirerCode || acquirerName;

  const resetFilters = () => {
    setAcquirerCode("");
    setAcquirerName("");
    setRid("");
    setPagination({ ...pagination, pageNumber: 1 });
    setReset(true);
  };

  const [acquirer, setAcquirer] = useState<MapAcquirers[]>([]);
  const [acquirerOnView, setAcquirerOnView] = useState<MapAcquirers | null>(
    null
  );
  const displayAddEditModal = (MapAcquirers: MapAcquirers | null) => {
    setMapAcquirerOnEdit(MapAcquirers);
    setShowAddEditModal(true);
  };

  const dismissAddEditModal = () => {
    setMapAcquirerOnEdit(null);
    setShowAddEditModal(false);
  };

  const fetchMapAcquirer = async (
    pageNumber = pagination.pageNumber,
    pageSize = pagination.pageSize
  ) => {
    if (rid && !/^\d+$/.test(rid)) {
      notify("error", "RID can only contain numeric characters");
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data, error, message } = await getAcquirer(
      pageNumber,
      pageSize,
      rid,
      acquirerCode,
      acquirerName
    );
    setLoading(false);

    if (error || !data) {
      notify("error", `Unable to fetch acquirer: ${message}`);
      return;
    }

    setAcquirer(data.records);
    setPagination({
      ...pagination,
      pageNumber,
      total: data.total
    });
    setReset(false);
  };

  const handleViewReport = async () => {
    setLoading(true);
    try {
      setPagination({ ...pagination, pageNumber: 1 });
      await fetchMapAcquirer(pagination.pageNumber, pagination.pageSize);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      notify("error", "An error occurred while fetching map acquirer.", error);
    }
  };

  useEffect(() => {
    if (!reset) {
      fetchMapAcquirer();
    } else {
      setPagination({ ...initialPaginationData });
      setReset(false);
    }
  }, [reset]);

  const columns: Column<MapAcquirers>[] = [
    {
      title: "RID",
      dataIndex: "rid",
      key: "rid"
    },
    {
      title: "Acquirer Code",
      dataIndex: "acquirerCode",
      key: "acquirerCode"
    },
    {
      title: "Acquirer Name",
      dataIndex: "acquirerName",
      key: "acquirerName"
    },
    {
      title: "Auditable Id",
      dataIndex: "auditableId",
      key: "auditableId"
    },

    {
      title: "",
      render: (_: unknown, mapAcquirers: MapAcquirers) => {
        const options: ContextOption[] = [
          {
            label: "Edit",
            onClick: () => {
              displayAddEditModal(mapAcquirers);
            }
          },
          {
            label: "View details",
            onClick: () => {
              setAcquirerOnView(mapAcquirers);
            }
          }
        ].filter(BooleanFilter) as ContextOption[];
        return <ContextMenu options={options} />;
      }
    }
  ];

  return (
    <main>
      <h1 className="title vertical-margin spaced thin">Map Acquirer to RID</h1>

      <div className="vertical-margin spaced">
        <div className={style.cont}>
          <div className={style.container}>
            <div className={style.cont1}>
              <div className="input-group half-width">
                <Input
                  value={rid}
                  onChange={(value: string) => setRid(value)}
                  placeholder="Enter rid"
                />
              </div>

              <div className="input-group half-width">
                <Input
                  value={acquirerName}
                  onChange={(value: string) => setAcquirerName(value)}
                  placeholder="Enter Acquirer name"
                />
              </div>

              <div className="input-group half-width">
                <Input
                  value={acquirerCode}
                  onChange={(value: string) => setAcquirerCode(value)}
                  placeholder="Enter Acquirer Code"
                />
              </div>
            </div>

            <div className={style.inputView}>
              <Button
                type="accent"
                size="large"
                className="strong no-pdf-print"
                onClick={handleViewReport}
              >
                Filter record
              </Button>

              <span className={style.button} />
            </div>
          </div>
        </div>
        {showResetFilter && (
          <Button type="accent" size="small" onClick={resetFilters}>
            RESET FILTERS
          </Button>
        )}
      </div>

      <br />
      <br />

      <div className="vertical-margin flex end">
        <Button onClick={() => displayAddEditModal(null)}>
          Map Appcode to RID
        </Button>
      </div>

      <Table
        dataSource={acquirer || []}
        columns={columns}
        pagination={pagination}
        onPageChange={fetchMapAcquirer}
        onPageSizeChange={pageSize =>
          setPagination({ ...pagination, pageSize, pageNumber: 1 })
        }
        loading={loading}
        className="smaller"
      />

      <AcquirerViewModal
        mapAcquirer={acquirerOnView}
        cancel={() => setAcquirerOnView(null)}
        refresh={fetchMapAcquirer}
        visible={Boolean(acquirerOnView)}
      />

      <AddEditModal
        visible={showAddEditModal}
        mapAcquirerOnEdit={mapAcquirerOnEdit}
        cancel={dismissAddEditModal}
        refresh={stayOnPage =>
          fetchMapAcquirer(stayOnPage ? pagination.pageNumber : 1)
        }
      />
    </main>
  );
};

const blankappService: MapAcquirerUpdate = {
  rid: "",
  acquirerCode: "",
  acquirerName: ""
};
const AddEditModal: FunctionComponent<
  ModalProps & {
    mapAcquirerOnEdit: MapAcquirers | null;
    refresh: (stayOnPage: boolean) => void;
    cancel: () => void;
  }
> = ({ visible, cancel, mapAcquirerOnEdit, refresh }) => {
  const [formData, setFormData] = useState<MapAcquirerUpdate>({
    ...blankappService,
    ...mapAcquirerOnEdit
  });

  const [isSaving, setIsSaving] = useState(false);
  const { notify } = useContext(LayoutContext);
  const [loading, setLoading] = useState(false);

  const handleChange = (key: keyof MapAcquirerUpdate, value: unknown) => {
    setFormData(prevFormData => ({ ...prevFormData, [key]: value }));
  };

  useEffect(() => {
    setFormData(
      mapAcquirerOnEdit
        ? { ...blankappService, ...mapAcquirerOnEdit }
        : { ...blankappService }
    );
  }, [visible, mapAcquirerOnEdit]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    // eslint-disable-next-line no-restricted-globals
    if (isNaN(Number(formData.rid))) {
      notify("error", "RID must be a number");
      return;
    }

    if (formData.rid.length > 11) {
      notify("error", "Rid should have a maximum length of 11 digits");
      return;
    }
    setIsSaving(true);
    setLoading(true);

    let id: number | undefined;

    if (mapAcquirerOnEdit && typeof mapAcquirerOnEdit.id === "number") {
      id = mapAcquirerOnEdit.id;
    }

    if (id !== undefined) {
      const { error, message, data } = await updateMapAcquirer(id, {
        ...formData
      });

      if (error) {
        notify("error", `Unable to update map acquirer: ${message}`);
        setIsSaving(false);
        return;
      }

      notify("success", "Successfully updated map acquirer");
    } else {
      const { error, message, data } = await mapAcquirerToRID({
        ...formData
      });

      if (error) {
        notify("error", `Unable to map acquirer to RID: ${message}`);
        setIsSaving(false);
        return;
      }

      notify("success", "Successfully mapped acquirer to RID");
    }
    cancel?.();
    refresh(false);
    setIsSaving(false);
  };

  useEffect(() => {
    if (!isCreateOperation && mapAcquirerOnEdit) {
      setFormData({
        rid: mapAcquirerOnEdit.rid,
        acquirerCode: mapAcquirerOnEdit.acquirerCode,
        acquirerName: mapAcquirerOnEdit.acquirerName
      });
    }
  }, [visible, mapAcquirerOnEdit]);

  const isCreateOperation = mapAcquirerOnEdit === null;

  return (
    <Modal cancel={cancel} visible={visible} size="small">
      <form onSubmit={handleSubmit}>
        <div className="input-group">
          <span className="question">RID</span>
          <Input
            value={formData.rid}
            onChange={value => handleChange("rid", value)}
            placeholder="Enter rid"
            responsive
            required
            disabled={!isCreateOperation}
          />
        </div>

        <div className="input-group">
          <div className={styles.span}>
            <span className="question">Acquirer Name</span>
          </div>
          <Input
            value={formData.acquirerName}
            onChange={value => handleChange("acquirerName", value)}
            placeholder="Enter Acquirer Name"
            responsive
            required
          />
        </div>

        <div className="input-group">
          <div className={styles.span}>
            <span className="question">Acquirer Code</span>
          </div>
          <Input
            value={formData.acquirerCode}
            onChange={value => handleChange("acquirerCode", value)}
            placeholder="Enter Acquirer Code"
            responsive
            required
          />
        </div>

        <br />
        <div className="flex spaced end vertical-margin">
          <Button type="accent" onClick={() => cancel?.()}>
            Cancel
          </Button>
          <Button buttonType="submit" loading={isSaving}>
            {mapAcquirerOnEdit ? "Edit Map acquirer" : "Create Map acquirer"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

interface mapAcquirerEditModalProps {
  mapAcquirer: MapAcquirers | null;
  cancel: () => void;
  refresh: () => void;
  visible: boolean;
}

const AcquirerViewModal: FunctionComponent<mapAcquirerEditModalProps> = ({
  mapAcquirer: _mapAcquirer,
  cancel,
  visible,
  refresh
}) => {
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState<MapAcquirers | null>(_mapAcquirer);

  useEffect(() => {
    setServices(_mapAcquirer);
  }, [_mapAcquirer]);

  return (
    <Modal visible={visible} cancel={cancel} ignoreBackdropClicks>
      {loading ? (
        <img
          alt="loading"
          src={resources.images.LOADING_TYPING}
          className="generic-icon xxl"
        />
      ) : (
        <section className={styles.details}>
          <div className={styles.detail}>
            <span className={styles.key}>Id:</span>
            <span className={styles.value}>{services?.id || "N/A"}</span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>RID:</span>
            <span className={styles.value}>{services?.rid || "N/A"}</span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>Acquirer Code:</span>
            <span className={styles.value}>
              {services?.acquirerCode || "N/A"}
            </span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>Acquirer Name :</span>
            <span className={styles.value}>
              {services?.acquirerName || "N/A"}
            </span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>Auditable Name:</span>
            <span className={styles.value}>
              {services?.auditableName || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Auditable Id:</span>
            <span className={styles.value}>
              {services?.auditableId || "N/A"}
            </span>
          </div>
        </section>
      )}
    </Modal>
  );
};

export default MapAcquirer;
